// Route configuration for SEO-optimized pages
export interface RouteConfig {
  path: string;
  title: string;
  seoTitle?: string; // Override for <title> tag (browser tab + Google). If not set, uses title.
  subtitle?: string;
  description: string;
  keywords: string;
  ogTitle: string;
  ogDescription: string;
  twitterTitle: string;
  twitterDescription: string;
  breadcrumbName: string;
  heroImage?: string;
  featuredImage?: string;
  heroImageAlt?: string;
  heroThemeIndex?: number;
  heroKicker?: string;
  heroBadgeText?: string;
  ctaLabel?: string;
}

export const routeConfigs: { [key: string]: RouteConfig } = {
  // Default route
  "/": {
    path: "/",
    title: "Turn Your Idea into a Living Masterpiece",
    seoTitle: "Fable AI Story Generator | Illustrated Stories, Audio & Video",
    subtitle:
      "Fable turns your idea into a complete story with narration, artwork, music, and video. Built for parents, authors, educators, and creators who want story creation to feel effortless.",
    description:
      "Create illustrated stories with Fable Go, or direct the details with Fable Gambit on the web. Bring your characters, voices, music and imagination.",
    keywords:
      "ai story app, ai story generator, ai storybook creator, ai story maker, personalized bedtime stories, ai storytelling app, ai bedtime story generator, create custom stories, story narration ai, ai character voices, fairy tales generator, adventure stories for kids",
    ogTitle: "Fable AI Story Generator | Illustrated Stories, Audio & Video",
    ogDescription:
      "Create illustrated stories with Fable Go, or direct the details with Fable Gambit on the web. Bring your characters, voices, music and imagination.",
    twitterTitle: "Fable AI Story Generator | Illustrated Stories, Audio & Video",
    twitterDescription:
      "Create illustrated stories with Fable Go, or direct the details with Fable Gambit on the web. Bring your characters, voices, music and imagination.",
    breadcrumbName: "Home",
    heroImage: "/hero/Home-Main.jpeg",
    featuredImage: "/hero/Home-Main.jpeg",
    heroImageAlt:
      "Fable personalized AI storybook hero with a child knight and friendly dragon",
    heroThemeIndex: 4,
    heroKicker: "Your story deserves to be told",
    heroBadgeText: "Get your first free story in less than 60 seconds!",
    ctaLabel: "Download Fable",
  },

  "/create-your-own-bedtime-stories": {
    path: "/create-your-own-bedtime-stories",
    title: "Create Your Own Bedtime Story",
    seoTitle:
      "Create Your Own Bedtime Stories | Personalized Bedtime Story App",
    subtitle:
      "Create a calming bedtime story with beautiful illustrations, narration, and music before lights out.",
    description:
      "Turn tonight's routine into a story made just for your child. Create illustrated bedtime stories with narration, music, and familiar characters in minutes.",
    keywords:
      "create your own bedtime stories, bedtime stories, bedtime stories for kids, personalized bedtime stories, short bedtime stories, bedtime story app, AI bedtime story generator, custom bedtime stories",
    ogTitle: "Create Your Own Bedtime Stories with Fable",
    ogDescription:
      "Create personalized bedtime stories with illustrations, narration, and music. Make your child the hero of tonight's story.",
    twitterTitle: "Create Your Own Bedtime Stories with Fable",
    twitterDescription:
      "Create personalized bedtime stories with illustrations, narration, and music. Make your child the hero of tonight's story.",
    breadcrumbName: "Create Your Own Bedtime Stories",
    heroImage: "/hero/Create-Your-Own-Bedtime-Story.jpeg",
    featuredImage: "/hero/Create-Your-Own-Bedtime-Story.jpeg",
    heroImageAlt:
      "Personalized bedtime story scene for kids created with Fable",
    heroThemeIndex: 3,
    heroKicker: "A calmer bedtime starts with a story made for them",
    heroBadgeText: "Create a cozy bedtime story in under 60 seconds!",
    ctaLabel: "Create a free bedtime story",
  },

  "/create-your-own-anime": {
    path: "/create-your-own-anime",
    title: "Generate Epic Anime Adventures",
    seoTitle: "Create Your Own Anime Story | AI Anime Story Generator",
    subtitle:
      "Build anime-inspired adventures with original characters, narration, music, and video.",
    description:
      "Design an anime-style story with custom characters, vivid illustrations, narration, music, and video. Make your child, friend, or original hero the star.",
    keywords:
      "create your own anime, AI anime story generator, anime story maker, anime character story, anime story app, AI story generator, custom anime stories, anime adventure generator",
    ogTitle: "Create Your Own Anime Story with Fable",
    ogDescription:
      "Turn your idea into an anime-style illustrated story with custom characters, narration, music, and video.",
    twitterTitle: "Create Your Own Anime Story with Fable",
    twitterDescription:
      "Turn your idea into an anime-style illustrated story with custom characters, narration, music, and video.",
    breadcrumbName: "Create Your Own Anime",
    heroImage: "/hero/Generate-Epic-Anime-Adventures.jpeg",
    featuredImage: "/hero/Generate-Epic-Anime-Adventures.jpeg",
    heroImageAlt: "Anime style custom story character generated with Fable",
    heroThemeIndex: 2,
    heroKicker: "Your anime arc starts here",
    heroBadgeText: "Build an anime-style story in under 60 seconds!",
    ctaLabel: "Create a free anime story",
  },

  "/ai-story-generator": {
    path: "/ai-story-generator",
    title: "AI Story Generator with Pictures and Narration",
    seoTitle:
      "AI Story Generator | Create Stories with Pictures, Audio, and Video",
    subtitle:
      "Start with a simple prompt, and turn it into a complete story with beautiful illustrations, music, narration and more.",
    description:
      "Describe any idea and Fable turns it into a complete illustrated story with consistent characters, narration, music, video, and a saved library.",
    keywords:
      "ai story generator, ai storygenerator, ai storyline generator, ai storytelling generator, story generator, story gen, story generator ai, AI story app, AI story maker",
    ogTitle: "AI Story Generator with Pictures, Narration, Music, and Video",
    ogDescription:
      "Use Fable to create complete AI stories with illustrations, narration, music, video, and consistent characters.",
    twitterTitle:
      "AI Story Generator with Pictures, Narration, Music, and Video",
    twitterDescription:
      "Use Fable to create complete AI stories with illustrations, narration, music, video, and consistent characters.",
    breadcrumbName: "AI Story Generator",
    heroImage: "/hero/Never-Run-Out-of-Story-Ideas-Again.jpeg",
    featuredImage: "/hero/Never-Run-Out-of-Story-Ideas-Again.jpeg",
    heroImageAlt:
      "AI story generator app creating a space adventure with custom characters",
    heroThemeIndex: 2,
    heroKicker: "One idea becomes a complete story world",
    heroBadgeText: "Generate your first AI story in under 60 seconds!",
    ctaLabel: "Create your story",
  },

  "/bedtime-stories-for-kids": {
    path: "/bedtime-stories-for-kids",
    title: "Make Your Child the Hero of Their Story",
    seoTitle: "Bedtime Stories for Kids | Personalized AI Bedtime Stories",
    subtitle:
      "Turn their name, photo, and favorite ideas into stories they feel are their own.",
    description:
      "Create gentle, illustrated bedtime stories for kids where your child becomes the hero. Add narration and music for an easier nightly routine.",
    keywords:
      "bedtime stories for kids, bedtime stories for preschoolers, bedtime stories for 5 year olds, bedtime story app, personalized stories for kids, bedtime stories to read, kids bedtime stories",
    ogTitle: "Personalized Bedtime Stories for Kids",
    ogDescription:
      "Create gentle AI bedtime stories for kids with custom characters, illustrations, narration, and music.",
    twitterTitle: "Personalized Bedtime Stories for Kids",
    twitterDescription:
      "Create gentle AI bedtime stories for kids with custom characters, illustrations, narration, and music.",
    breadcrumbName: "Bedtime Stories for Kids",
    heroImage: "/hero/Make-Your-Child-the-Hero-of-Their-Story.jpeg",
    featuredImage: "/hero/Make-Your-Child-the-Hero-of-Their-Story.jpeg",
    heroImageAlt:
      "Child listening to a personalized bedtime story in the Fable app",
    heroThemeIndex: 3,
    heroKicker: "Stories that help kids settle in",
    heroBadgeText: "Create a kid-friendly bedtime story tonight!",
    ctaLabel: "Start a bedtime story",
  },

  "/short-bedtime-stories": {
    path: "/short-bedtime-stories",
    title: "Create a Short Bedtime Story in Seconds",
    seoTitle: "Short Bedtime Stories | 5-Minute Personalized Stories",
    subtitle:
      "When bedtime is getting late, create a short story that is quick, high quality, and ready in a few minutes.",
    description:
      "Need a quick bedtime story? Fable creates short, personalized stories with pictures and narration when you only have a few minutes.",
    keywords:
      "short bedtime stories, short bedtime story, 5 min bedtime story, quick bedtime stories, fast bedtime stories, bedtime story short story, small bedtime story",
    ogTitle: "Short Bedtime Stories Created in Minutes",
    ogDescription:
      "Create quick personalized bedtime stories with illustrations and narration for busy family nights.",
    twitterTitle: "Short Bedtime Stories Created in Minutes",
    twitterDescription:
      "Create quick personalized bedtime stories with illustrations and narration for busy family nights.",
    breadcrumbName: "Short Bedtime Stories",
    heroImage: "/hero/Create-a-Short-Bedtime-Story-in-Seconds.jpeg",
    featuredImage: "/hero/Create-a-Short-Bedtime-Story-in-Seconds.jpeg",
    heroImageAlt: "Short personalized bedtime story preview made with Fable",
    heroThemeIndex: 0,
    heroKicker: "Five quiet minutes can still feel magical",
    heroBadgeText: "Create a short bedtime story before lights out!",
    ctaLabel: "Make a short story",
  },

  "/ai-story-generator-free": {
    path: "/ai-story-generator-free",
    title: "AI Story Generator For Kids",
    seoTitle: "Free AI Story Generator | Try Fable Free",
    subtitle:
      "Create kid-friendly illustrated stories with narration, music, and video with minimal effort.",
    description:
      "Try Fable free and create a personalized AI story with images, narration, music, and video. Start with an idea, photo, character, or theme.",
    keywords:
      "ai story generator free, free ai story generator, ai story generator free unlimited, ai story generator free no sign up, free story generator, story maker ai, AI story creator",
    ogTitle: "Free AI Story Generator - Try Fable Free",
    ogDescription:
      "Create your first personalized AI story free with pictures, narration, music, and video.",
    twitterTitle: "Free AI Story Generator - Try Fable Free",
    twitterDescription:
      "Create your first personalized AI story free with pictures, narration, music, and video.",
    breadcrumbName: "Free AI Story Generator",
    heroImage: "/hero/AI-Story-Generator-For-Kids.jpeg",
    featuredImage: "/hero/AI-Story-Generator-For-Kids.jpeg",
    heroImageAlt:
      "Free AI story generator creating a personalized illustrated adventure",
    heroThemeIndex: 1,
    heroKicker: "Try the magic before you commit",
    heroBadgeText: "Your first Fable story is free!",
    ctaLabel: "Create a free story",
  },

  "/ai-story-writer": {
    path: "/ai-story-writer",
    title: "AI Story Builder For Creators",
    seoTitle: "AI Story Writer | Write Illustrated Stories with Fable",
    subtitle:
      "Build polished illustrated stories from prompts with characters, scenes, narration, music, and video.",
    description:
      "Use Fable as an AI story writer that turns prompts into polished stories with visuals, voices, music, and reusable characters.",
    keywords:
      "ai story writer, ai story write, story writing ai, ai story maker, ai story creator, narrative generator, storyline maker, story prompt generator",
    ogTitle: "AI Story Writer for Illustrated Stories",
    ogDescription:
      "Turn prompts into polished illustrated stories with custom characters, narration, music, and video.",
    twitterTitle: "AI Story Writer for Illustrated Stories",
    twitterDescription:
      "Turn prompts into polished illustrated stories with custom characters, narration, music, and video.",
    breadcrumbName: "AI Story Writer",
    heroImage: "/hero/AI-Story-Builder-For-Creators.jpeg",
    featuredImage: "/hero/AI-Story-Builder-For-Creators.jpeg",
    heroImageAlt:
      "Creator using Fable as an AI story writer for illustrated stories",
    heroThemeIndex: 2,
    heroKicker: "Write less. Bring more of the story to life",
    heroBadgeText: "Turn a prompt into a story in under 60 seconds!",
    ctaLabel: "Write a free story",
  },

  "/book-publisher": {
    path: "/book-publisher",
    title: "Create, Print, & Ship Your Own Storybooks with No Experience",
    seoTitle:
      "Personalized Book Publisher | Create and Print Custom Storybooks",
    subtitle:
      "Publishing your own story has never been easier. Creating your first book is a simple prompt away.",
    description:
      "Create a personalized storybook in Fable, then turn your favorite stories into beautiful printed books for gifts, classrooms, and family shelves.",
    keywords:
      "book publisher, AI book publisher, personalized book publisher, custom storybook publisher, print personalized storybook, create printed children books, bedtime story books, AI book generator",
    ogTitle: "Create and Print Personalized Storybooks with Fable",
    ogDescription:
      "Use Fable to create personalized stories and turn them into printed storybooks and keepsakes.",
    twitterTitle: "Create and Print Personalized Storybooks with Fable",
    twitterDescription:
      "Use Fable to create personalized stories and turn them into printed storybooks and keepsakes.",
    breadcrumbName: "Personalized Book Publisher",
    heroImage: "/hero/Create-Print-Ship-Your-Own-Storybooks-with-No-Experience.jpeg",
    featuredImage: "/hero/Create-Print-Ship-Your-Own-Storybooks-with-No-Experience.jpeg",
    heroImageAlt:
      "Printed personalized children book created from a Fable AI story",
    heroThemeIndex: 4,
    heroKicker: "Make the story real enough to hold",
    heroBadgeText: "Create a printable storybook in minutes!",
    ctaLabel: "Create a printable story",
  },

  // Create story books route
  "/create-story-books-with-ai": {
    path: "/create-story-books-with-ai",
    subtitle:
      "Create the perfect gift with a personalized story, and solidify it in time with a beautiful printed keepsake.",
    title: "Replace Expensive Printed Storybooks with AI",
    description:
      "Turn any idea into an illustrated children's story book with AI. 11 art styles, personalized characters, print-ready in minutes. Free to try.",
    keywords:
      "create story books with ai, ai picture book generator, illustrated children's books ai, custom story book creator, printable kids books ai, ai storybook maker",
    ogTitle: "Create AI Story Books for Kids with Fable",
    ogDescription:
      "Turn any idea into an illustrated children's story book with AI. 11 art styles, personalized characters, print-ready in minutes. Free to try.",
    twitterTitle: "Create AI Story Books for Kids with Fable",
    twitterDescription:
      "Turn any idea into an illustrated children's story book with AI. 11 art styles, personalized characters, print-ready in minutes. Free to try.",
    breadcrumbName: "Create Story Books with AI",
    heroImage: "/hero/Replace-Expensive-Printed-Storybooks-with-AI.jpeg",
    featuredImage: "/hero/Replace-Expensive-Printed-Storybooks-with-AI.jpeg",
    heroImageAlt:
      "AI picture book generator creating a custom children storybook",
    heroThemeIndex: 4,
    heroKicker: "From idea to illustrated book",
    heroBadgeText: "Create a storybook your child can star in!",
    ctaLabel: "Create an AI story book",
  },

  "/classroom-stories-students-want-to-read": {
    path: "/classroom-stories-students-want-to-read",
    title: "Create Classroom Stories Students Actually Want to Read",
    seoTitle: "Create Classroom Stories Students Actually Want to Read | Fable",
    subtitle:
      "Turn lessons and goals into personalized stories with characters your students will care about.",
    description:
      "Turn lessons, vocabulary, classroom themes, and reading goals into illustrated stories students are excited to finish.",
    keywords:
      "classroom stories, classroom story maker, stories for students, AI stories for teachers, educational story generator, personalized learning stories",
    ogTitle: "Create Classroom Stories Students Actually Want to Read",
    ogDescription:
      "Use Fable to turn lessons and classroom ideas into personalized illustrated stories with narration and music.",
    twitterTitle: "Create Classroom Stories Students Actually Want to Read",
    twitterDescription:
      "Use Fable to turn lessons and classroom ideas into personalized illustrated stories with narration and music.",
    breadcrumbName: "Classroom Stories",
    heroImage: "/hero/Create-Classroom-Stories-Students-Actually-Want-to-Read.jpeg",
    featuredImage: "/hero/Create-Classroom-Stories-Students-Actually-Want-to-Read.jpeg",
    heroImageAlt:
      "Students engaged with a personalized classroom story created in Fable",
    heroThemeIndex: 1,
    heroKicker: "Lessons become stories they remember",
    heroBadgeText: "Create a classroom story in minutes!",
    ctaLabel: "Create a classroom story",
  },

  "/bible-story-generator": {
    path: "/bible-story-generator",
    title: "Create Bible Stories for Kids",
    seoTitle: "Bible Story Generator for Kids | Personalized Bible Stories",
    subtitle:
      "Bring faith, morals, and timeless lessons to life through the art of AI stories.",
    description:
      "Create gentle, personalized Bible-inspired stories that help young readers connect with faith, morals, and meaningful lessons.",
    keywords:
      "bible story generator, bible stories for kids, personalized bible stories, christian stories for kids, moral stories for kids, AI bible story generator",
    ogTitle: "Create Personalized Bible Stories for Kids",
    ogDescription:
      "Use Fable to create personalized Bible-inspired stories for kids with illustrations, narration, and music.",
    twitterTitle: "Create Personalized Bible Stories for Kids",
    twitterDescription:
      "Use Fable to create personalized Bible-inspired stories for kids with illustrations, narration, and music.",
    breadcrumbName: "Bible Story Generator",
    heroImage: "/hero/Create-Bible-Stories-for-Kids.jpeg",
    featuredImage: "/hero/Create-Bible-Stories-for-Kids.jpeg",
    heroImageAlt: "Personalized moral story for kids created with Fable",
    heroThemeIndex: 4,
    heroKicker: "Faith lessons become stories kids can feel",
    heroBadgeText: "Create a personalized Bible story in minutes!",
    ctaLabel: "Create a Bible story",
  },
};

// Helper function to get route config by path
export const getRouteConfig = (path: string): RouteConfig => {
  return routeConfigs[path] || routeConfigs["/"];
};

// Helper function to get all route paths
export const getAllRoutePaths = (): string[] => {
  return Object.keys(routeConfigs);
};

// 301 redirect map for removed routes
export const routeRedirects: Record<string, string> = {
  "/ai-story-maker": "/",
  "/ai-story-creator": "/",
  "/ai-book-publisher": "/book-publisher",
  "/publish-personalized-storybooks-from-anywhere": "/book-publisher",
  "/replace-expensive-printed-storybooks-with-ai":
    "/create-story-books-with-ai",
  "/make-your-child-the-hero-of-every-story": "/bedtime-stories-for-kids",
  "/generate-epic-anime-adventures": "/create-your-own-anime",
  "/create-your-own-bedtime-story": "/create-your-own-bedtime-stories",
  "/never-run-out-of-story-ideas-again": "/ai-story-generator",
  "/ai-picture-book-generator": "/create-story-books-with-ai",
  "/create-your-own-storybook": "/create-story-books-with-ai",
  "/personalized-bedtime-stories": "/create-your-own-bedtime-stories",
};
